import { decamelizeKeys } from 'humps';
import { isEmpty } from 'lodash';
import { v4 as uuid } from 'uuid';

import {
  ConversationMessage,
  ConversationMessageSource,
  ConversationStreamEvent,
  ExternalEntity,
  ListConversationsApiV1ConversationsGetApiArg,
  MessageContext as ServerMessageContext,
  Conversation as ServerConversation,
  ResumeScope,
  UserRating,
} from '~/api/sylvanus/typedefs.autogen';
import { SearchQuery } from '~/features/advanced-search/types';
import {
  ANCHOR_ORDERS,
  AnswerAnchor,
  FeedbackDetails as ChatFeedbackDetails,
  Message as ChatMessage,
  Citation,
  CitationEntity,
  MessageSource,
  SearchResultItem,
  SearchStatus,
} from '~/features/ask-anything/types/chat';
import { SearchEntity } from '~/features/ask-anything/types/chatbot';
import { postProcessMarkdown } from '~/features/ask-anything/utils/postProcessMarkdown';
import { Entity, EntityType } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import {
  createNewConversation,
  deleteConversation as deleteConversationRequest,
  getAllConversations,
  getConversationHistoryPage,
  getExistingConversation,
  patchConversation as patchConversationRequest,
  postNewUserMessage,
  postUserRating,
} from '../methods/conversationalAi';
import { testIsAxiosError } from '../utils';
import { camelizeKeys, ParsedType } from './utils';

export type Message = ParsedType<ConversationMessage>;
export type Conversation = ParsedType<ServerConversation>;
export type MessageStreamEvent = ParsedType<ConversationStreamEvent>;
export interface ClientConversation {
  id: Uuid;
  created: string;
  title?: string | null;
  version?: number;
  createdBy: string;
  userEmail: string;
  messages: ChatMessage[];
  metadata?: {
    documentHash: string;
  };
  /**
   * Best-effort validation of the last user message's scope, computed by the
   * server on GET. Absent/null does not imply the scope is valid - it may
   * simply not have been computed. See EKP-54886.
   */
  resumeScope?: ResumeScope | null;
  /**
   * Entity the conversation was created against. Resume compares its pre-sig /
   * post-sig class against the current surface before restoring scope.
   */
  sourceEntity?: ExternalEntity | null;
}

interface MessageContext {
  search_query?: SearchQuery;
  search_entity: SearchEntity;
  selected_ids?: string[];
  entities_in_view?: Entity<CitationEntity>[];
}
export interface PostUserMessageParams {
  conversationId: Uuid;
  message: string;
  context?: MessageContext[];
  metadata?: {
    questionId: Uuid;
    questionGroupId: Uuid;
  };
}

export interface PostUserFeedbackParams {
  conversationId: Uuid;
  messageId: Uuid;
  feedbackDetails: ChatFeedbackDetails;
}

interface ClientPageConversation {
  items: Omit<Conversation, 'messages'>[];
  total: number;
  page?: number;
  size?: number;
  pages?: number;
}

export type ConversationHistoryPageParams = Pick<
  ListConversationsApiV1ConversationsGetApiArg,
  'cursor' | 'pageSize'
>;

export interface ConversationHistoryPage {
  items: Omit<Conversation, 'messages'>[];
  cursor: string | null;
}

export interface PatchConversationParams {
  conversationId: Uuid;
  title: string;
}

export interface DeleteConversationParams {
  conversationId: Uuid;
}

export const getConversations = async (
  params: Entity<EntityType>,
): Promise<ClientPageConversation> => {
  const serverConversations = await getAllConversations({
    entityId: params.id,
    entityType: params.type as ExternalEntity['type'],
  });
  // TODO remove ts-ignore when server response is fixed
  //@ts-ignore
  return camelizeKeys(serverConversations);
};

export const getConversationHistory = async (
  params: ConversationHistoryPageParams,
): Promise<ConversationHistoryPage> => {
  const serverPage = await getConversationHistoryPage({
    cursor: params.cursor ?? '',
    pageSize: params.pageSize ?? 20,
  });
  // TODO remove ts-ignore when server response is fixed
  //@ts-ignore
  return camelizeKeys(serverPage);
};

export const createConversation = async (
  entity: Entity<EntityType>,
): Promise<Conversation | undefined> => {
  try {
    const response = await createNewConversation(entity as ExternalEntity);
    //@ts-ignore
    return camelizeKeys(response);
  } catch (error: unknown) {
    if (testIsAxiosError(error))
      //i18n-ignore: string needs to be BE handle
      throw new Error('Failed to create conversation', {
        cause: error.response?.status,
      });
    throw new Error();
  }
};

export const getConversation = async ({
  conversationId,
}: {
  conversationId: Uuid;
}): Promise<ClientConversation> => {
  const response = await getExistingConversation(conversationId);
  const conversation = {
    id: response.id,
    created: response.datetime_created,
    title: response.title,
    version: response.version,
    createdBy: response.created_by_user_id,
    userEmail: response.created_by_user_email,
    messages: response.messages.map(mapServerMessageToChatMessage),
    metadata: response.metadata,
    resumeScope: response.resume_scope,
    sourceEntity: response.entities?.[0]?.source_entity,
  };
  // TODO remove ts-ignore when server response is fixed
  //@ts-ignore
  return conversation;
};

export const patchConversation = async ({
  conversationId,
  title,
}: PatchConversationParams): Promise<Conversation> => {
  const latest = await getExistingConversation(conversationId);
  const version = latest.version;
  if (version == null) {
    throw new Error('Conversation version is required to rename.');
  }

  const response = await patchConversationRequest({
    conversationId,
    updateConversationRequest: { title, version },
  });

  // TODO remove ts-ignore when server response is fixed
  //@ts-ignore
  return camelizeKeys(response);
};

export const deleteConversation = async ({
  conversationId,
}: DeleteConversationParams): Promise<void> => {
  await deleteConversationRequest(conversationId);
};

export const postUserMessage = async ({
  conversationId,
  message,
  context,
  metadata,
}: PostUserMessageParams): Promise<ChatMessage> => {
  const response = await postNewUserMessage({
    conversationId,
    message,
    context,
    metadata,
  });
  return mapServerMessageToChatMessage(response);
};

export const postUserFeedback = async ({
  conversationId,
  messageId,
  feedbackDetails,
}: PostUserFeedbackParams): Promise<ChatMessage> => {
  const response = await postUserRating({
    conversationId,
    messageId,
    userRating: mapUserRatingFromFeedbackDetails(feedbackDetails),
    userComment: feedbackDetails.comment,
    userCategory: feedbackDetails.category,
  });
  return mapServerMessageToChatMessage(response);
};

/**
 * Map the answer strip's facts onto the anchor the chat bubble renders.
 * A half-formed payload reads as absent, so no component sees a partial fact.
 *
 * @param raw - The strip metadata, in either raw snake_case or camelized form.
 * @returns The anchor, or undefined when it names no result, ranking field or end.
 */
const mapAnswerAnchor = (
  raw: Record<string, any> | undefined | null,
): AnswerAnchor | undefined => {
  const topResult = raw?.top_result ?? raw?.topResult;
  const sortedByLabel = topResult?.sorted_by_label ?? topResult?.sortedByLabel;
  // The order picks which sentence gets rendered, so an unknown one has no copy to fall back on.
  const hasOrder = ANCHOR_ORDERS.includes(topResult?.order);
  if (!raw || !topResult?.name || !sortedByLabel || !hasOrder) {
    return undefined;
  }
  const dateRange = raw.date_range ?? raw.dateRange;
  return {
    intentCategory: raw.intent_category ?? raw.intentCategory ?? null,
    topResult: {
      name: topResult.name,
      id: topResult.id,
      sortedBy: topResult.sorted_by ?? topResult.sortedBy,
      sortedByLabel,
      order: topResult.order,
      date: topResult.date,
    },
    dateRange,
  };
};

/**
 * Maps the full-search skill's structured metadata off a message's `additional_info`.
 *
 * @param additionalInfo - The message metadata, in either raw snake_case or camelized form.
 * @returns The search-handoff fields for the chat message; empty when no metadata is present.
 */
const mapSearchHandoffFields = (
  additionalInfo: Record<string, any> | undefined | null,
): Pick<
  ChatMessage,
  | 'constructedSearchQuery'
  | 'searchStatus'
  | 'searchResultCount'
  | 'searchResultItems'
  | 'answerAnchor'
> => {
  if (!additionalInfo) {
    return {};
  }
  return {
    constructedSearchQuery:
      additionalInfo.constructed_search_query ??
      additionalInfo.constructedSearchQuery,
    searchStatus: (additionalInfo.search_status ??
      additionalInfo.searchStatus) as SearchStatus | undefined,
    searchResultCount:
      additionalInfo.search_result_count ?? additionalInfo.searchResultCount,
    searchResultItems: (additionalInfo.search_result_items ??
      additionalInfo.searchResultItems) as SearchResultItem[] | undefined,
    answerAnchor: mapAnswerAnchor(
      additionalInfo.answer_strip_metadata ??
        additionalInfo.answerStripMetadata,
    ),
  };
};

export const mapServerMessageContextToChatMessageContext = (
  messageContext: ServerMessageContext,
): ChatMessage['messageContext'] => ({
  searchQuery: messageContext.search_query as SearchQuery | undefined,
  searchEntity: messageContext.search_entity as SearchEntity | undefined,
  selectedIds: messageContext.selected_ids ?? undefined,
  entitiesInView: messageContext.entities_in_view as
    | Entity<CitationEntity>[]
    | undefined,
  messageType: messageContext.message_type ?? undefined,
});

const pickFirstMessageContext = <T>(contexts: T[] | null | undefined) =>
  contexts?.[0];

const mapMessageContextFromServerMessage = (
  response: Pick<ConversationMessage, 'message_context'>,
): ChatMessage['messageContext'] => {
  const context = pickFirstMessageContext(response.message_context);
  return context
    ? mapServerMessageContextToChatMessageContext(context)
    : undefined;
};

const mapMessageContextFromParsedMessage = (
  message: Message,
): ChatMessage['messageContext'] => {
  const contexts = message.messageContext;
  const context = pickFirstMessageContext(
    Array.isArray(contexts) ? contexts : undefined,
  );
  return context
    ? mapServerMessageContextToChatMessageContext(
        decamelizeKeys(context) as ServerMessageContext,
      )
    : undefined;
};

export const mapServerMessageToChatMessage = (
  response: ConversationMessage,
): ChatMessage => {
  const mappedCitations = mapCitations(response);

  const { citations, text } = postProcessMarkdown(
    response.message_text,
    mappedCitations,
  );
  return {
    choices: response.response_suggestions ?? [],
    id: response.id,
    sources: (response.sources ?? []).map(
      (source: ConversationMessageSource) =>
        ({
          ...camelizeKeys(source),
          id: uuid(),
        }) as unknown as MessageSource,
    ),
    text,
    citations,
    //l10n-ignore: API parsing no formatting required
    timestamp: new Date(response.datetime_created),
    userType: response.role === 'user' ? 'current' : 'other',
    feedbackDetails: mapFeedbackDetailsFromUserRating(
      response.user_rating ?? null,
    ),
    ...mapSearchHandoffFields(response.additional_info),
    messageContext: mapMessageContextFromServerMessage(response),
  };
};

export const parseServerMessage = (message: Message): ChatMessage => {
  const mappedCitations = mapCitationsFromParsedMessage(message);

  const { citations, text } = postProcessMarkdown(
    message.messageText,
    mappedCitations,
  );
  const parsedMessage: ChatMessage = {
    choices: (message.responseSuggestions ?? []) as string[],
    id: message.id,
    sources: ((message.sources ?? []) as ConversationMessageSource[]).map(
      (source) =>
        ({
          ...camelizeKeys(source),
          id: uuid(),
        }) as unknown as MessageSource,
    ),
    text,
    //l10n-ignore: API parsing no formatting required
    timestamp: new Date(message.datetimeCreated),
    userType: message.role === 'user' ? 'current' : 'other',
    feedbackDetails: mapFeedbackDetailsFromUserRating(
      message.userRating ?? null,
    ),
    citations,
    ...mapSearchHandoffFields(message.additionalInfo),
    messageContext: mapMessageContextFromParsedMessage(message),
  };

  return parsedMessage;
};

export const mapFeedbackDetailsFromUserRating = (
  rating: Nullable<UserRating>,
): ChatFeedbackDetails => {
  switch (rating) {
    case 'positive':
      return { value: true };
    case 'negative':
      return { value: false };
    default:
      return { value: null };
  }
};

export const mapCitations = (
  message: Pick<ConversationMessage, 'citations'>,
): Record<string, Citation> => {
  if (!message.citations || isEmpty(message.citations)) return {};

  const citationMap: Record<string, Citation> = Object.fromEntries(
    Object.entries(message.citations ?? {}).map(([key, citation]) => [
      key,
      {
        key,
        highlights: citation.highlights.map((highlight) => ({
          id: uuid(),
          coordinates: highlight.coordinates ?? [],
          htmlTokens: highlight.html_tokens ?? [],
        })),
        originalQuote: citation.original_quote,
        entity: citation.entity,
        link: citation.link,
        tooltip: citation.tooltip,
        entityName: citation.entity_name ?? null,
      },
    ]),
  );
  return citationMap;
};

const mapCitationsFromParsedMessage = (
  message: Message,
): Record<string, Citation> => {
  if (!message.citations || isEmpty(message.citations)) return {};

  const citationMap: Record<string, Citation> = Object.fromEntries(
    Object.entries(message.citations ?? {}).map(([key, citation]) => [
      key,
      {
        key,
        highlights: citation.highlights.map((highlight) => ({
          id: uuid(),
          coordinates: highlight.coordinates ?? [],
          htmlTokens: highlight.htmlTokens ?? [],
        })),
        originalQuote: citation.originalQuote,
        entity: citation.entity,
        link: citation.link,
        tooltip: citation.tooltip,
        entityName: citation.entityName,
      } as Citation,
    ]),
  );
  return citationMap;
};

export const mapUserRatingFromFeedbackDetails = (
  details: Nullable<ChatFeedbackDetails> | undefined,
): Nullable<UserRating> => {
  if (!details) return null;
  const { value } = details;
  switch (value) {
    case true:
      return 'positive';
    case false:
      return 'negative';
    default:
      return null;
  }
};
